// Help.jsx - Help & FAQ page for CodeRated
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, 
  ChevronDown, 
  ChevronRight, 
  Eye, 
  Brain, 
  Zap, 
  CreditCard, 
  Shield, 
  MessageCircle,
  Book,
  LifeBuoy,
  Mail,
  Phone,
  FileText,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  Info,
  ArrowRight,
  Clock
} from 'lucide-react';

export default function Help() {
  const [searchQuery, setSearchQuery] = useState('');
  const [openCategories, setOpenCategories] = useState(['getting-started']);
  const [openFAQs, setOpenFAQs] = useState([]);

  const toggleCategory = (categoryId) => {
    setOpenCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleFAQ = (faqId) => {
    setOpenFAQs(prev => 
      prev.includes(faqId) 
        ? prev.filter(id => id !== faqId)
        : [...prev, faqId]
    );
  };

  const supportChannels = [
    {
      icon: MessageCircle,
      title: 'Live Chat',
      description: 'Chat with our support team',
      availability: 'Mon-Fri, 9 AM - 6 PM PST',
      action: 'Start Chat',
      href: '#',
      color: 'blue'
    },
    {
      icon: Mail,
      title: 'Email Support',
      description: 'Send us a detailed message',
      availability: 'Response within 4 hours',
      action: 'Send Email',
      href: '/contact',
      color: 'green'
    },
    {
      icon: Phone,
      title: 'Enterprise Support',
      description: 'Dedicated support for Business+ plans',
      availability: '24/7 for Business customers',
      action: 'Call Us',
      href: 'tel:+15551234567',
      color: 'purple'
    }
  ];

  const quickGuides = [
    {
      title: 'Getting Started Guide',
      description: 'Complete walkthrough for new users',
      icon: Book,
      time: '5 min read',
      href: '#getting-started'
    },
    {
      title: 'Understanding AI Analysis',
      description: 'How our multi-agent AI works',
      icon: Brain,
      time: '3 min read',
      href: '#ai-analysis'
    },
    {
      title: 'API Documentation',
      description: 'Integrate CodeRated into your workflow',
      icon: FileText,
      time: '10 min read',
      href: '/api-docs'
    },
    {
      title: 'Billing & Subscriptions',
      description: 'Manage your account and billing',
      icon: CreditCard,
      time: '2 min read',
      href: '#billing'
    }
  ];

  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: LifeBuoy,
      faqs: [
        {
          id: 'how-it-works',
          question: 'How does CodeRated work?',
          answer: 'CodeRated uses a multi-agent AI system where 5 specialized AI agents analyze different aspects of your website: design & UX, performance, SEO, accessibility, and content. Each agent provides expert-level insights that are combined into a comprehensive report with actionable recommendations.'
        },
        {
          id: 'what-analyzed',
          question: 'What exactly gets analyzed on my website?',
          answer: 'Our AI analyzes visual design elements, page load speeds, mobile responsiveness, SEO optimization, accessibility compliance, content quality, user experience patterns, and technical performance. We also provide ROI predictions and competitive benchmarking where applicable.'
        },
        {
          id: 'accuracy',
          question: 'How accurate are the AI insights?',
          answer: 'Our AI analysis maintains 95% accuracy compared to human expert evaluations. However, AI insights should be used as guidance alongside your professional judgment. Complex business context and brand-specific requirements may require human expertise.'
        },
        {
          id: 'free-trial',
          question: 'Is there a free version?',
          answer: 'Yes! Our Observer plan is completely free and includes 10 website analyses per month, basic scoring, and access to our public feed. You can upgrade to Professional or Business plans for unlimited analyses and advanced features.'
        }
      ]
    },
    {
      id: 'ai-analysis',
      title: 'AI Analysis',
      icon: Brain,
      faqs: [
        {
          id: 'multi-agent',
          question: 'What is multi-agent AI?',
          answer: 'Multi-agent AI means we use 5 specialized AI models that work together like a consulting team. Each agent focuses on a specific area (UX design, performance, SEO, accessibility, content) and they collaborate to provide comprehensive insights that no single AI model could achieve alone.'
        },
        {
          id: 'analysis-time',
          question: 'How long does analysis take?',
          answer: 'Most website analyses complete in 2-3 seconds. Complex sites with many pages or heavy media might take up to 10 seconds. Our distributed computing infrastructure ensures fast processing while maintaining thorough analysis quality.'
        },
        {
          id: 'mobile-analysis',
          question: 'Do you analyze mobile versions?',
          answer: 'Yes! Our AI automatically analyzes both desktop and mobile versions of your website. We check responsive design, mobile-specific performance, touch interactions, and mobile UX patterns to ensure your site works great on all devices.'
        },
        {
          id: 'private-sites',
          question: 'Can you analyze password-protected or private sites?',
          answer: 'Currently, we can only analyze publicly accessible websites. For private sites, enterprise customers can use our on-premise solution or contact our team for custom integration options.'
        }
      ]
    },
    {
      id: 'billing',
      title: 'Billing & Plans',
      icon: CreditCard,
      faqs: [
        {
          id: 'plan-differences',
          question: 'What\'s the difference between plans?',
          answer: 'Observer (Free): 10 analyses/month, basic insights. Professional ($49/mo): Unlimited analyses, advanced AI insights, API access, export reports. Business ($149/mo): Everything in Pro + white-label reports, team collaboration, dedicated support, higher API limits.'
        },
        {
          id: 'upgrade-downgrade',
          question: 'Can I upgrade or downgrade anytime?',
          answer: 'Yes! You can change plans anytime from your account settings. Upgrades take effect immediately, downgrades take effect at your next billing cycle. Usage limits adjust according to your new plan.'
        },
        {
          id: 'refunds',
          question: 'Do you offer refunds?',
          answer: 'We offer full refunds within 30 days of purchase if you\'re not satisfied. After 30 days, we don\'t provide refunds for partial billing periods, but you can cancel anytime to avoid future charges.'
        },
        {
          id: 'enterprise-pricing',
          question: 'Do you have enterprise pricing?',
          answer: 'Yes! We offer custom enterprise solutions with volume discounts, dedicated infrastructure, custom integrations, and SLA guarantees. Contact our sales team for a personalized quote based on your needs.'
        }
      ]
    },
    {
      id: 'technical',
      title: 'Technical',
      icon: Zap,
      faqs: [
        {
          id: 'api-access',
          question: 'How do I use the API?',
          answer: 'Professional and Business plans include API access. You\'ll get an API key from your dashboard to make programmatic requests. Our REST API supports batch analysis, webhooks, and real-time results. Check our API documentation for detailed integration guides.'
        },
        {
          id: 'data-security',
          question: 'How secure is my data?',
          answer: 'We use enterprise-grade security including SSL encryption, secure servers, regular security audits, and SOC 2 compliance. Website URLs and analysis results are encrypted at rest and in transit. We never store sensitive content from your websites.'
        },
        {
          id: 'rate-limits',
          question: 'Are there rate limits?',
          answer: 'Observer: 10 analyses/month. Professional: Unlimited website analyses + 1,000 API calls/month. Business: Unlimited analyses + 10,000 API calls/month. Enterprise: Custom limits based on your needs.'
        },
        {
          id: 'uptime',
          question: 'What\'s your uptime guarantee?',
          answer: 'We maintain 99.9% uptime with redundant infrastructure across multiple regions. Business and Enterprise customers get SLA guarantees. Check our status page for real-time system health and incident history.'
        }
      ]
    },
    {
      id: 'account',
      title: 'Account Management',
      icon: Shield,
      faqs: [
        {
          id: 'team-access',
          question: 'Can I add team members?',
          answer: 'Business plans include 10 team seats with role-based permissions. Team members can collaborate on analyses, share reports, and access shared workspaces. Additional seats are available for $15/month each.'
        },
        {
          id: 'export-data',
          question: 'Can I export my analysis data?',
          answer: 'Professional and Business plans can export analysis reports as PDF, CSV, or JSON. You can also use our API to programmatically access all your historical analysis data for integration with other tools.'
        },
        {
          id: 'delete-account',
          question: 'How do I delete my account?',
          answer: 'You can delete your account from Settings > Account > Delete Account. This permanently removes all your data within 30 days. If you have an active subscription, it will be cancelled immediately.'
        },
        {
          id: 'forgot-password',
          question: 'I forgot my password. How do I reset it?',
          answer: 'Click "Forgot Password" on the login page and enter your email. We\'ll send you a secure reset link that expires in 1 hour. If you don\'t receive it, check your spam folder or contact support.'
        }
      ]
    }
  ];

  // Filter FAQs based on search query
  const filteredCategories = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.faqs.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      {/* Navigation */}
      <nav className="relative z-50 bg-slate-900/80 backdrop-blur-xl border-b border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="group flex items-center gap-4 p-2 hover:scale-110 transition-all duration-300">
              <div className="relative">
                <div className="w-12 h-10 bg-gradient-to-b from-gray-300 to-gray-500 rounded-lg border-2 border-gray-600 shadow-xl">
                  <div className="w-8 h-5 bg-black mx-auto mt-1 rounded border border-gray-800 flex items-center justify-center">
                    <Eye className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div className="flex justify-center mt-0.5 gap-1">
                    <div className="w-1 h-0.5 bg-red-500 rounded"></div>
                    <div className="w-1 h-0.5 bg-green-500 rounded"></div>
                  </div>
                </div>
              </div>
              <span className="text-2xl font-bold text-white">CodeRated</span>
            </Link>
            
            <div className="flex items-center gap-4">
              <Link to="/contact" className="text-slate-300 hover:text-white transition-colors">
                Contact Support
              </Link>
              <Link to="/" className="text-slate-300 hover:text-white transition-colors">
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-20 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          
          {/* Hero Section */}
          <section className="text-center mb-16">
            <h1 className="text-6xl md:text-7xl font-black mb-8">
              <span className="bg-gradient-to-r from-white via-blue-100 to-purple-200 bg-clip-text text-transparent">
                Help Center
              </span>
            </h1>
            
            <p className="text-2xl md:text-3xl text-slate-300 mb-8 font-light max-w-4xl mx-auto">
              Find answers, get support, and learn how to make the most of 
              <span className="text-blue-400 font-semibold"> CodeRated's AI platform</span>.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-12">
              <div className="relative">
                <div className="absolute left-6 top-1/2 transform -translate-y-1/2 text-slate-400">
                  <Search className="w-5 h-5" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for help articles..."
                  className="w-full pl-14 pr-6 py-4 bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 text-lg"
                />
              </div>
            </div>
          </section>

          {/* Quick Support Options */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Need Immediate Help?</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {supportChannels.map((channel, index) => {
                const IconComponent = channel.icon;
                return (
                  <Link
                    key={index}
                    to={channel.href}
                    className="group relative"
                  >
                    <div className={`absolute -inset-1 bg-gradient-to-r from-${channel.color}-500/20 to-${channel.color}-600/20 rounded-2xl blur opacity-0 group-hover:opacity-100 transition duration-500`} />
                    <div className="relative bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl p-6 hover:bg-slate-900/80 transition-all duration-300 text-center h-full">
                      <div className={`w-16 h-16 bg-gradient-to-r from-${channel.color}-500 to-${channel.color}-600 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-2">{channel.title}</h3>
                      <p className="text-slate-400 mb-3">{channel.description}</p>
                      <div className="flex items-center justify-center gap-1 text-slate-500 text-sm mb-4">
                        <Clock className="w-4 h-4" />
                        <span>{channel.availability}</span>
                      </div>
                      <div className={`inline-flex items-center gap-2 text-${channel.color}-400 font-semibold group-hover:text-${channel.color}-300 transition-colors`}>
                        <span>{channel.action}</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* Quick Guides */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Popular Guides</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {quickGuides.map((guide, index) => {
                const IconComponent = guide.icon;
                return (
                  <Link
                    key={index}
                    to={guide.href}
                    className="group bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-xl p-6 hover:bg-slate-900/80 transition-all duration-300"
                  >
                    <IconComponent className="w-8 h-8 text-blue-400 mb-4 group-hover:scale-110 transition-transform duration-300" />
                    <h3 className="text-lg font-bold text-white mb-2 group-hover:text-blue-300 transition-colors">{guide.title}</h3>
                    <p className="text-slate-400 text-sm mb-3">{guide.description}</p>
                    <div className="flex items-center text-slate-500 text-xs">
                      <Clock className="w-3 h-3 mr-1" />
                      <span>{guide.time}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>

          {/* FAQ Section */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">Frequently Asked Questions</h2>
            
            <div className="max-w-4xl mx-auto">
              {filteredCategories.length === 0 ? (
                <div className="text-center py-12">
                  <HelpCircle className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No results found</h3>
                  <p className="text-slate-400">Try a different search term or browse our categories below.</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {filteredCategories.map((category) => {
                    const IconComponent = category.icon;
                    const isOpen = openCategories.includes(category.id);
                    
                    return (
                      <div key={category.id} className="bg-slate-900/60 backdrop-blur-xl border border-slate-700/50 rounded-2xl overflow-hidden">
                        <button
                          onClick={() => toggleCategory(category.id)}
                          className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-900/80 transition-all duration-300"
                        >
                          <div className="flex items-center gap-4">
                            <IconComponent className="w-6 h-6 text-blue-400" />
                            <h3 className="text-xl font-bold text-white">{category.title}</h3>
                          </div>
                          {isOpen ? (
                            <ChevronDown className="w-5 h-5 text-slate-400" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          )}
                        </button>
                        
                        {isOpen && (
                          <div className="border-t border-slate-700/50">
                            {category.faqs.map((faq) => {
                              const isFAQOpen = openFAQs.includes(faq.id);
                              
                              return (
                                <div key={faq.id} className="border-b border-slate-800/50 last:border-b-0">
                                  <button
                                    onClick={() => toggleFAQ(faq.id)}
                                    className="w-full p-6 text-left flex items-center justify-between hover:bg-slate-800/30 transition-all duration-300"
                                  >
                                    <span className="text-white font-medium pr-4">{faq.question}</span>
                                    {isFAQOpen ? (
                                      <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                                    )}
                                  </button>
                                  
                                  {isFAQOpen && (
                                    <div className="px-6 pb-6">
                                      <p className="text-slate-300 leading-relaxed">{faq.answer}</p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Still Need Help */}
          <section className="text-center">
            <div className="bg-gradient-to-r from-blue-900/20 to-purple-900/20 backdrop-blur-xl border border-blue-500/30 rounded-3xl p-12">
              <h2 className="text-3xl font-bold text-white mb-6">Still Need Help?</h2>
              <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
                Can't find what you're looking for? Our support team is here to help you succeed.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2"
                >
                  <Mail className="w-5 h-5" />
                  <span>Contact Support</span>
                </Link>
                <Link
                  to="/search"
                  className="border border-slate-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-slate-800/50 transition-all flex items-center justify-center gap-2"
                >
                  <Eye className="w-5 h-5" />
                  <span>Try Our Demo</span>
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}